import { Check, Loader2, Minus, Zap } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { COMPARISON, KITS } from "@/data/kits";
import { useCheckout } from "@/lib/api";

const Cell = ({ v }) =>
  v === true ? (
    <Check className="w-5 h-5 text-blaze mx-auto" />
  ) : v ? (
    <span className="font-mono text-[10px] uppercase tracking-wide text-slate-300">{v}</span>
  ) : (
    <Minus className="w-4 h-4 text-slate-600 mx-auto" />
  );

export default function Pricing() {
  const { buy, loading } = useCheckout();

  return (
    <section id="kits" className="relative py-24 lg:py-32 bg-panel/40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          overline="The Arsenal // Three-Kit Command Matrix"
          title="Pick your tier. Upgrade when you outgrow it."
          sub="Every kit downloads instantly after secure Square checkout. Start with Starter and upgrade later — you only pay the difference."
          align="center"
        />

        <div className="mt-16 grid lg:grid-cols-3 gap-6 items-stretch">
          {KITS.map((kit, i) => (
            <Reveal key={kit.id} delay={i * 0.12} className="h-full">
              <div
                data-testid={`tier-card-${kit.id.replace("-kit", "").replace("-system", "")}`}
                className={`relative h-full flex flex-col bg-panel p-8 transition-colors duration-200 ${
                  kit.popular
                    ? "border-2 border-blaze lg:-translate-y-3 shadow-[0_0_45px_rgba(249,115,22,0.12)]"
                    : "border border-slate-400/10 hover:border-blaze/40"
                }`}
              >
                {kit.popular && <div className="absolute top-0 inset-x-0 h-1.5 hazard-stripes" />}
                <p className={`font-mono text-[10px] uppercase tracking-[0.22em] font-semibold ${kit.popular ? "text-blaze" : "text-slate-500"}`}>
                  {kit.badge}
                </p>
                <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-slate-50">
                  {kit.name}
                </h3>
                <div className="mt-4 flex items-end gap-2">
                  <span className="font-mono font-bold tracking-tighter text-5xl text-slate-50">${kit.price}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 pb-2">one-time / forever</span>
                </div>
                <p className="mt-3 text-xs font-mono uppercase tracking-[0.14em] text-slate-400">{kit.target}</p>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{kit.blurb}</p>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {kit.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-blaze mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`btn-checkout-${kit.id.replace("-kit", "").replace("-system", "")}`}
                  onClick={() => buy(kit.id)}
                  disabled={loading !== null}
                  className={`mt-8 w-full inline-flex items-center justify-center gap-2 font-display font-bold uppercase tracking-wide text-base px-6 py-4 transition-[transform,box-shadow,background-color,color] duration-200 disabled:opacity-60 ${
                    kit.popular
                      ? "bg-blaze text-white clip-notch-sm hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(249,115,22,0.4)]"
                      : "border border-slate-400/30 text-slate-100 hover:border-blaze/60 hover:text-blaze"
                  }`}
                >
                  {loading === kit.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
                  Get {kit.short} — ${kit.price}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-10 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
            Upgrade path: Starter → Shop Builder → Complete. Pay only the difference, keep your files.
          </p>
        </Reveal>

        <div id="compare" className="mt-20 scroll-mt-24">
          <Reveal>
            <h3 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-50 text-center">
              Feature-by-feature
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 overflow-x-auto border border-slate-400/10 bg-panel">
              <table className="w-full min-w-[640px] text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-slate-400/10">
                    <th className="text-left p-5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Document</th>
                    <th className="p-5 font-display font-bold uppercase text-slate-200">Starter<br /><span className="font-mono text-xs text-blaze">$67</span></th>
                    <th className="p-5 font-display font-bold uppercase text-blaze bg-blaze/5">Shop Builder<br /><span className="font-mono text-xs">$147</span></th>
                    <th className="p-5 font-display font-bold uppercase text-slate-200">Complete<br /><span className="font-mono text-xs text-blaze">$297</span></th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row, i) => (
                    <tr key={i} className="border-b border-slate-400/5 last:border-0 hover:bg-raised/60 transition-colors">
                      <td className="p-4 pl-5 text-slate-300">{row.feature}</td>
                      <td className="p-4 text-center"><Cell v={row.starter} /></td>
                      <td className="p-4 text-center bg-blaze/5"><Cell v={row.builder} /></td>
                      <td className="p-4 text-center"><Cell v={row.complete} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
